import { useEffect, useState } from "react";
import { registerPhoneWebhook } from "../apis/whatsappAccount";

const EMBEDDED_SIGNUP_CONFIG_ID = "1404119364652323";
const REDIRECT_URI = "https://pingfront.ngrok-free.app/whatsapp/callback";

const WhatsappBusiness = () => {
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [reVerifyingPhoneId, setReVerifyingPhoneId] = useState(null);

  // Fetch WhatsApp accounts
  const fetchWhatsappAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      if (!token) return;

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (data.success) {
        setAccounts(data.data || []);
        if (data.data?.length > 0) setConnected(true);
      } else {
        setError(data.error || "Failed to load WhatsApp accounts");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Server error while fetching WhatsApp accounts");
    } finally {
      setLoading(false);
    }
  };

  // Re-verify phone number and register webhook
  const handleReVerifyPhone = async (phoneId) => {
    try {
      setReVerifyingPhoneId(phoneId);
      const token = localStorage.getItem("userAccessToken");
      if (!token) {
        setError("⚠️ Authentication token not found. Please log in again.");
        return;
      }

      const response = await registerPhoneWebhook(token, phoneId);
      if (response.data.success) {
        setMessage(
          "✅ Phone number re-verified and webhook registered successfully!"
        );
        setError("");
        fetchWhatsappAccounts();
      } else {
        setError(response.data.message || "Failed to re-verify phone number.");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Server error while re-verifying phone number. Please try again."
      );
    } finally {
      setReVerifyingPhoneId(null);
    }
  };

  // Delete WhatsApp Phone Number
  const handleDeletePhoneNumber = async (phone_id) => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect this WhatsApp number?"
      )
    )
      return;

    try {
      const token = localStorage.getItem("userAccessToken");
      if (!token) return;

      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts/${phone_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();

      if (data.success) {
        setMessage("✅ WhatsApp number disconnected successfully!");
        fetchWhatsappAccounts();
      } else {
        setError(data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("❌ Delete error:", err);
      setError("Server error while disconnecting phone number.");
    } finally {
      setLoading(false);
    }
  };

  // Send code to backend
  const sendCodeToBackend = async (code) => {
    const token = localStorage.getItem("userAccessToken");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/facebook/embedded-signup/callback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code }),
          credentials: "include",
        }
      );
      const data = await res.json();
      setMessage("🎉 WhatsApp onboarding successful!");
      setError("");
      fetchWhatsappAccounts();
      console.log("Backend response:", data);
    } catch (error) {
      console.error("Failed to send signup code to backend:", error);
      setError("Failed to complete onboarding. Please try again.");
    }
  };

  // Handle Embedded Signup
  const handleEmbeddedSignup = () => {
    setLoading(true);
    setError("");
    setMessage("");

    const token = localStorage.getItem("userAccessToken");
    if (!token) {
      setError("⚠️ Please log in to your account first.");
      setLoading(false);
      return;
    }

    if (window.FB && typeof window.FB.login === "function") {
      window.FB.login(
        (response) => {
          setLoading(false);
          if (response.authResponse && response.authResponse.code) {
            const code = response.authResponse.code;
            console.log("FB login code received:", code);
            sendCodeToBackend(code);
          } else {
            console.warn(
              "No auth code received in FB login response",
              response
            );
            setError("Failed to get authorization code. Please try again.");
          }
        },
        {
          config_id: EMBEDDED_SIGNUP_CONFIG_ID,
          response_type: "code",
          override_default_response_type: true,
          redirect_uri: REDIRECT_URI,
        }
      );
    } else {
      setLoading(false);
      setError("Facebook SDK not loaded. Please refresh and try again.");
    }
  };

  const handleRetry = () => {
    setError("");
    setMessage("");
    setLoading(false);
    fetchWhatsappAccounts();
  };

  // Check if phone has verification or webhook issues
  const hasVerificationIssue = (account) => {
    return (
      account.code_verification_status === "EXPIRED" ||
      account.is_webhook_registered === 0
    );
  };

  // Get appropriate error message
  const getErrorMessage = (account) => {
    if (account.code_verification_status === "EXPIRED") {
      return "⚠️ Verification code has expired. Please re-verify this number.";
    }
    if (account.is_webhook_registered === 0) {
      return `⚠️ Webhook registration failed: ${
        account.webhook_registration_error || "Unknown error"
      }`;
    }
    return "";
  };

  useEffect(() => {
    fetchWhatsappAccounts();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary rounded-2xl shadow-lg mb-4">
            <svg
              className="w-12 h-12 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>

          <p className="text-gray-600 text-lg">
            Connect and manage your business numbers seamlessly
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="max-w-2xl mx-auto mb-8 bg-white border-l-4 border-red-500 rounded-lg shadow-md p-5">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-red-800 font-medium">{error}</p>
                <button
                  onClick={handleRetry}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {message && !error && (
          <div className="max-w-2xl mx-auto mb-8 bg-white border-l-4 border-green-500 rounded-lg shadow-md p-5">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="w-6 h-6 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-green-800 font-medium">{message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading Spinner */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="animate-spin h-16 w-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <svg
                  className="w-6 h-6 text-emerald-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
            </div>
            <p className="mt-4 text-gray-700 font-medium text-lg">
              Processing your request...
            </p>
          </div>
        )}

        {/* No Connections State */}
        {!connected && !loading && !error && accounts.length === 0 && (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full mb-6">
                <svg
                  className="w-14 h-14 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Connect Your Business
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Link your Facebook account to start managing WhatsApp Business
                numbers and engage with your customers
              </p>
              <button
                onClick={handleEmbeddedSignup}
                disabled={loading}
                className="w-full bg-primary text-white font-semibold px-8 py-4 rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <span className="flex items-center justify-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Connect with Facebook
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Connected Accounts */}
        {connected && accounts.length > 0 && !loading && (
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Connected Numbers
                  </h2>
                  <p className="text-gray-600">
                    Manage your WhatsApp Business accounts
                  </p>
                </div>
                <button
                  onClick={handleEmbeddedSignup}
                  className="bg-primary font-semibold px-6 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center whitespace-nowrap text-white"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Number
                </button>
              </div>

              <div className="space-y-6">
                {accounts.map((wabaAccount) => (
                  <div
                    key={wabaAccount.id}
                    className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                  >
                    {/* Business Header */}
                    <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                        <h3 className="text-xl md:text-2xl font-bold text-white flex items-center">
                          <svg
                            className="w-6 h-6 mr-3 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                          </svg>
                          <span className="break-all">
                            {wabaAccount.account_name}
                          </span>
                        </h3>
                        <button className="px-5 py-2.5 bg-white text-teal-700 rounded-lg hover:bg-teal-50 transition-colors duration-200 text-sm font-semibold shadow-md border border-teal-100 whitespace-nowrap">
                          Manage Account
                        </button>
                      </div>
                    </div>

                    <div
                      key={wabaAccount.phone_id}
                      className="bg-white border-2 border-gray-100 p-5 rounded-xl hover:border-teal-200 hover:shadow-md transition-all duration-200"
                    >
                      {/* Verification/Webhook Error Alert */}
                      {hasVerificationIssue(wabaAccount) && (
                        <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg
                                className="w-5 h-5 text-amber-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div className="ml-3 flex-1">
                              <p className="text-sm text-amber-800 font-medium mb-3">
                                {getErrorMessage(wabaAccount)}
                              </p>
                              <button
                                onClick={() =>
                                  handleReVerifyPhone(wabaAccount.phone_id)
                                }
                                disabled={
                                  reVerifyingPhoneId === wabaAccount.phone_id
                                }
                                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all duration-200 text-sm font-semibold shadow-md hover:shadow-lg transform hover:scale-105 flex items-center disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                              >
                                {reVerifyingPhoneId === wabaAccount.phone_id ? (
                                  <>
                                    <svg
                                      className="animate-spin h-4 w-4 mr-2"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                      />
                                    </svg>
                                    Re-verify & Register
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Phone Info */}
                        <div className="flex items-start space-x-4 flex-1 min-w-0">
                          <div className="flex-shrink-0 w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center">
                            <svg
                              className="w-7 h-7 text-teal-600"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              Phone Number
                            </p>
                            <p className="text-lg md:text-xl font-bold text-gray-900 mb-1 break-all">
                              {wabaAccount.phone_number}
                            </p>
                            <p className="text-sm text-gray-600 flex items-center">
                              <svg
                                className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                />
                              </svg>
                              <span className="break-all">
                                {wabaAccount.phone_name}
                              </span>
                            </p>
                          </div>
                        </div>

                        {/* Delete Button */}
                        <button
                          onClick={() =>
                            handleDeletePhoneNumber(wabaAccount.phone_id)
                          }
                          className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 text-sm font-semibold shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center whitespace-nowrap"
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Disconnect
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappBusiness;
